import type { Nullable } from "models-webgl2";
import { CommandAction } from "utils/helpers/classes/command-controller";
import {
  Webgl2Component,
  CropSelectionComponent,
  CanvasResizeSelectionComponent,
  ImageRectComponent,
  ImageAdjustRectComponent,
} from "../../webgl2-component";
import { Texture } from "../../texture";
import { Webgl2dRenderer } from "../webgl2d-renderer";
import { Webgl2Tool } from "./webgl2-tool";
import { Vec2 } from "utils/math/vec2";
import { Mat3 } from "utils/math/mat3";
import { CropSelectionBottomComponent } from "../../webgl2-component/components/crop-selection-component/crop-selection-bottom-component";
import { CropSelectionBottomLeftComponent } from "../../webgl2-component/components/crop-selection-component/crop-selection-bottom-left-component";
import { CropSelectionBottomRightComponent } from "../../webgl2-component/components/crop-selection-component/crop-selection-bottom-right-component";
import { CropSelectionLeftComponent } from "../../webgl2-component/components/crop-selection-component/crop-selection-left-component";
import { CropSelectionRightComponent } from "../../webgl2-component/components/crop-selection-component/crop-selection-right-component";
import { CropSelectionTopComponent } from "../../webgl2-component/components/crop-selection-component/crop-selection-top-component";
import { CropSelectionTopLeftComponent } from "../../webgl2-component/components/crop-selection-component/crop-selection-top-left-component";
import { CropSelectionTopRightComponent } from "../../webgl2-component/components/crop-selection-component/crop-selection-top-right-component";

const DEFAULT_CURSOR = "default";
const CROP_CURSOR_AABB_MARGIN_PX = 40;
const CROP_WHEEL_SCALE_IN = 1.05;
const CROP_WHEEL_SCALE_OUT = 0.95;
// Minimum crop-box size on screen (CSS px). Converted to world px based on camera scale & DPR.
const CROP_WHEEL_MIN_SCREEN_SIZE_CSS_PX = 40;
// Hard lower bound in world px to avoid degenerate sizes.
const CROP_WHEEL_MIN_SIZE_PX = 18;

export class CropTool extends Webgl2Tool {
  private _currComponent: Nullable<Webgl2Component>;
  private _dispose: Nullable<VoidFunction>;
  private _lastCursor: string = DEFAULT_CURSOR;

  private get _selectedComponent(): Nullable<ImageRectComponent | ImageAdjustRectComponent> {
    const component = this._renderer.selectedComponent;
    return component instanceof ImageRectComponent || component instanceof ImageAdjustRectComponent
      ? component
      : null;
  }

  constructor(private readonly _renderer: Webgl2dRenderer) {
    super();
  }

  public onAssign() {
    this._renderer.cropSelectionComponent.setCenterCrosshairVisible(false);

    const component = this._selectedComponent;
    if (!component) return;

    this._renderer.cropSelectionComponent.copyComponentTransform(component);
    this._initCropSelectionCanvasCentered(component);
    this._setCursor(DEFAULT_CURSOR);
    this._dispose = this._renderer.commands.events.on("history", () => {
      this._renderer.eventsManager.assignTool(this._renderer.tools.handSelectionTool);
    });
  }

  public onUnAssign() {
    this._handleClearTool();
    this._renderer.cropSelectionComponent.setCenterCrosshairVisible(false);
    this._setCursor(DEFAULT_CURSOR);
  }

  public handleCrop() {
    const component = this._selectedComponent;
    if (!component) return this._handleClearTool();

    const ratio = component.state.texture.textureSize.clone().divide(component.transform.size);
    const undoTexture = component.state.texture.clone();
    const undoTransform = component.transform.clone();
    const prevCanvasSize = this._renderer.canvasSize.clone();
    const prevCanvasTranslation = this._renderer.canvasTranslation.clone();

    const actionTexture = component.state.texture.clone();
    const selectedTopLeft = Webgl2dRenderer.global.getComponentClipSpaceInGlobal(
      component,
      Vec2.zero(),
    );
    const cropTopLeftGlobal = Webgl2dRenderer.global.getComponentClipSpaceInGlobal(
      this._renderer.cropSelectionComponent,
      Vec2.zero(),
    );
    const cropBottomRightGlobal = Webgl2dRenderer.global.getComponentClipSpaceInGlobal(
      this._renderer.cropSelectionComponent,
      Vec2.one(),
    );
    const tm3 = Mat3.identity()
      .rotate(-component.transform.rads)
      .translate(selectedTopLeft.clone().negate());

    const cropTopLeftLocal = cropTopLeftGlobal.transformMat3(tm3.mat);
    const cropBottomRightLocal = cropBottomRightGlobal.transformMat3(tm3.mat);

    const minX = Math.max(0, Math.min(cropTopLeftLocal.x, cropBottomRightLocal.x));
    const maxX = Math.min(
      component.transform.size.width,
      Math.max(cropTopLeftLocal.x, cropBottomRightLocal.x),
    );
    const minY = Math.max(0, Math.min(cropTopLeftLocal.y, cropBottomRightLocal.y));
    const maxY = Math.min(
      component.transform.size.height,
      Math.max(cropTopLeftLocal.y, cropBottomRightLocal.y),
    );

    if (maxX <= minX || maxY <= minY) {
      return this._handleClearTool();
    }

    const cropSize = new Vec2(maxX - minX, maxY - minY).multiply(ratio);
    const cropOffset = new Vec2(minX, minY).multiply(ratio);

    Texture.cropTexture(
      component.state.texture,
      actionTexture,
      cropOffset.x,
      cropOffset.y,
      cropSize.width,
      cropSize.height,
    );
    const targetSize = actionTexture.textureSize.clone();

    this._renderer.commands.execute(
      new CommandAction(
        "Crop Tool",
        () => {
          this._renderer.setCanvasSize(targetSize, true);
          const actionTransform = this._renderer.cropSelectionComponent.transform.clone();
          actionTransform.calcDimensions({
            position: this._renderer.canvasGlobalCenter,
            size: targetSize,
            rotation: component.transform.degrees,
            scale: Vec2.one(),
          });
          this._renderer.selectComponent(component);
          component.transform.copy(actionTransform);
          component.state.texture = actionTexture.clone();
          this._renderer.eventsManager.assignTool(this._renderer.tools.handSelectionTool);
          this._renderer.selectionComponent.copyComponentTransform(component);
        },
        () => {
          this._renderer.setCanvasSize(prevCanvasSize, false);
          this._renderer.setCanvasTranslate(prevCanvasTranslation, true);
          this._renderer.selectComponent(component);
          component.transform.copy(undoTransform);
          component.state.texture = undoTexture.clone();
          this._renderer.eventsManager.assignTool(this._renderer.tools.cropTool);
          this._renderer.cropSelectionComponent.copyComponentTransform(component);
          this._renderer.selectionComponent.copyComponentTransform(component);
        },
      ),
    );
  }

  /**
   * Zoom crop selection with mouse wheel (when pointer is inside the crop box).
   * Returns true when event was handled.
   */
  public handleCropSelectionWheelZoom(
    e: WheelEvent,
    options?: { overflowPaddingPx?: number; minSizePx?: number },
  ): boolean {
    // Only handle when crop tool is active.
    if (!this._renderer.eventsManager.checkForTool(this)) return false;
    if (!this._selectedComponent) return false;

    const crop = this._renderer.cropSelectionComponent;
    // Wheel events can be emitted faster than render() (rAF). Ensure model matrix is up-to-date
    // so center/inside checks don't drift.
    crop.calcModelMatrix(undefined, true);

    if (!this._isMouseInsideCropSelection(e)) return false;

    const baseSize = crop.transform.size.getAbs();
    const scaleAbs = crop.transform.scale.getAbs();
    const effectiveSize = baseSize.clone().multiply(scaleAbs);
    if (effectiveSize.hasZero()) return false;

    const center = Webgl2dRenderer.global.getComponentClipSpaceInGlobal(crop, Vec2.half());

    const overflowPaddingPx =
      typeof options?.overflowPaddingPx === "number" && Number.isFinite(options.overflowPaddingPx)
        ? options.overflowPaddingPx
        : 0;

    const dpr = typeof window === "undefined" ? 1 : window.devicePixelRatio || 1;
    const cameraScale = this._renderer.camera.scale || 1;
    const minSizeFromScreenPx = (CROP_WHEEL_MIN_SCREEN_SIZE_CSS_PX * dpr) / cameraScale;
    const defaultMinSizePx =
      Number.isFinite(minSizeFromScreenPx) && minSizeFromScreenPx > 0
        ? Math.max(CROP_WHEEL_MIN_SIZE_PX, minSizeFromScreenPx)
        : CROP_WHEEL_MIN_SIZE_PX;

    const minSizePx =
      typeof options?.minSizePx === "number" && Number.isFinite(options.minSizePx)
        ? options.minSizePx
        : defaultMinSizePx;

    const canvasTopLeft = this._renderer.canvasTranslation.clone();
    const canvasBottomRight = this._renderer.canvasTranslation
      .clone()
      .add(this._renderer.canvasSize);

    const minX = Math.min(canvasTopLeft.x, canvasBottomRight.x) - overflowPaddingPx;
    const maxX = Math.max(canvasTopLeft.x, canvasBottomRight.x) + overflowPaddingPx;
    const minY = Math.min(canvasTopLeft.y, canvasBottomRight.y) - overflowPaddingPx;
    const maxY = Math.max(canvasTopLeft.y, canvasBottomRight.y) + overflowPaddingPx;

    const maxHalfW = Math.min(center.x - minX, maxX - center.x);
    const maxHalfH = Math.min(center.y - minY, maxY - center.y);
    if (!(maxHalfW > 0 && maxHalfH > 0)) return false;

    const maxScale = Math.min((maxHalfW * 2) / effectiveSize.x, (maxHalfH * 2) / effectiveSize.y);
    if (!(maxScale > 0)) return false;

    const safeMinSizePx = Math.max(0, minSizePx);
    const minScale = safeMinSizePx
      ? Math.max(safeMinSizePx / effectiveSize.x, safeMinSizePx / effectiveSize.y)
      : 0;

    const wheelScale = e.deltaY < 0 ? CROP_WHEEL_SCALE_IN : CROP_WHEEL_SCALE_OUT;
    let nextScale = wheelScale;
    if (nextScale < minScale) nextScale = minScale;
    if (nextScale > maxScale) nextScale = maxScale;
    if (!Number.isFinite(nextScale) || nextScale <= 0) return false;

    crop.transform.calcDimensions({
      position: center,
      size: baseSize.clone().multiply(nextScale),
      rotation: crop.transform.degrees,
      roundPosition: false,
    });
    this._renderer.render();
    return true;
  }

  public onMouseDown(e: MouseEvent) {
    return this._handleDown(e);
  }

  public onMouseMove(e: MouseEvent) {
    this._handleMove(e);
  }

  public onMouseOver(e: MouseEvent) {
    // While dragging, keep cursor based on current active component.
    if (this._currComponent) {
      this._setCursor(this._getCursorByComponent(this._currComponent));
      this._renderer.cropSelectionComponent.setCenterCrosshairVisible(true);
      return;
    }

    if (!this._selectedComponent) {
      this._renderer.cropSelectionComponent.setCenterCrosshairVisible(false);
      this._setCursor(DEFAULT_CURSOR);
      return;
    }

    // Avoid expensive picking when mouse is clearly outside crop selection area.
    if (!this._isMousePossiblyOverCropSelection(e)) {
      this._renderer.cropSelectionComponent.setCenterCrosshairVisible(false);
      this._setCursor(DEFAULT_CURSOR);
      return;
    }

    const hovered = this._renderer.findComponentUnderMouse(e);
    const root = Webgl2dRenderer.global.getRootParent(hovered).slice(-1)[0];

    if (root instanceof CropSelectionComponent) {
      this._renderer.cropSelectionComponent.setCenterCrosshairVisible(true);
      this._setCursor(this._getCursorByComponent(hovered));
      return;
    }

    this._renderer.cropSelectionComponent.setCenterCrosshairVisible(false);
    this._setCursor(DEFAULT_CURSOR);
  }

  public onMouseLeave() {
    this._renderer.cropSelectionComponent.setCenterCrosshairVisible(false);
    this._setCursor(DEFAULT_CURSOR);
  }

  public onMouseEnded(e: MouseEvent) {
    return this._handleUp(e);
  }

  public onTouchStart(e: TouchEvent) {
    return this._handleDown(e);
  }

  public onTouchMove(e: TouchEvent) {
    this._handleMove(e);
  }

  public onTouchEnded(e: TouchEvent) {
    return this._handleUp(e);
  }

  private _handleDown(e: MouseEvent | TouchEvent) {
    this._currComponent = this._renderer.findComponentUnderMouse(e);

    const parent = Webgl2dRenderer.global.getRootParent(this._currComponent).slice(-1)[0];

    if (parent instanceof CropSelectionComponent && this._currComponent?.onMouseDown) {
      this._currComponent?.onMouseDown(e);
    } else {
      // We are not interacting with crop selection; allow hand panning.
      this._currComponent = null;
      this._renderer.tools.handTool.handleDown(e);
    }
  }

  private _handleMove(e: MouseEvent | TouchEvent) {
    if (this._currComponent) {
      this._currComponent.onMouseMove?.(e);
    } else {
      this._renderer.tools.handTool.handleMove(e);
    }
  }

  private _handleUp(e: MouseEvent | TouchEvent) {
    this._currComponent?.onMouseUp?.(e);
    this._currComponent = null;
  }

  private _handleClearTool() {
    this._renderer.cropSelectionComponent.transform.reset();
    this._renderer.eventsManager.assignTool(this._renderer.tools.handSelectionTool);
    this._dispose?.();
    this._dispose = null;
    this._currComponent = null;
  }

  private _initCropSelectionCanvasCentered(
    component: ImageRectComponent | ImageAdjustRectComponent,
  ) {
    try {
      // Ensure model matrix is available for global calculations.
      component.calcModelMatrix(undefined, true);

      const canvasSize = this._renderer.canvasSize.clone().round();
      if (canvasSize.hasZero()) return;

      this._renderer.cropSelectionComponent.transform.calcDimensions({
        position: this._renderer.canvasGlobalCenter,
        size: canvasSize,
        rotation: 0,
      });
      this._renderer.render();
    } catch {
      // noop
    }
  }

  private _setCursor(cursor: string) {
    if (cursor === this._lastCursor) return;
    this._lastCursor = cursor;
    this._renderer.canvas.style.cursor = cursor;
  }

  private _isMousePossiblyOverCropSelection(e: MouseEvent): boolean {
    try {
      const mouse = this._renderer.camera.getGlobalMousePosition(e);
      const crop = this._renderer.cropSelectionComponent;
      // Use component corners to build AABB in global coords (works even if rotated).
      const tl = Webgl2dRenderer.global.getComponentClipSpaceInGlobal(crop, Vec2.zero());
      const tr = Webgl2dRenderer.global.getComponentClipSpaceInGlobal(crop, Vec2.oneZero());
      const br = Webgl2dRenderer.global.getComponentClipSpaceInGlobal(crop, Vec2.one());
      const bl = Webgl2dRenderer.global.getComponentClipSpaceInGlobal(crop, Vec2.zeroOne());

      const minX = Math.min(tl.x, tr.x, br.x, bl.x);
      const maxX = Math.max(tl.x, tr.x, br.x, bl.x);
      const minY = Math.min(tl.y, tr.y, br.y, bl.y);
      const maxY = Math.max(tl.y, tr.y, br.y, bl.y);

      const scale = this._renderer.camera.scale || 1;
      const margin = CROP_CURSOR_AABB_MARGIN_PX / scale;

      return (
        mouse.x >= minX - margin &&
        mouse.x <= maxX + margin &&
        mouse.y >= minY - margin &&
        mouse.y <= maxY + margin
      );
    } catch {
      return true;
    }
  }

  private _isMouseInsideCropSelection(e: WheelEvent): boolean {
    try {
      const mouse = this._renderer.camera.getGlobalMousePosition(e);
      const crop = this._renderer.cropSelectionComponent;
      const tl = Webgl2dRenderer.global.getComponentClipSpaceInGlobal(crop, Vec2.zero());
      const tr = Webgl2dRenderer.global.getComponentClipSpaceInGlobal(crop, Vec2.oneZero());
      const br = Webgl2dRenderer.global.getComponentClipSpaceInGlobal(crop, Vec2.one());
      const bl = Webgl2dRenderer.global.getComponentClipSpaceInGlobal(crop, Vec2.zeroOne());

      const minX = Math.min(tl.x, tr.x, br.x, bl.x);
      const maxX = Math.max(tl.x, tr.x, br.x, bl.x);
      const minY = Math.min(tl.y, tr.y, br.y, bl.y);
      const maxY = Math.max(tl.y, tr.y, br.y, bl.y);

      return mouse.x >= minX && mouse.x <= maxX && mouse.y >= minY && mouse.y <= maxY;
    } catch {
      return false;
    }
  }

  private _getCursorByComponent(component: Nullable<Webgl2Component>): string {
    if (!component) return DEFAULT_CURSOR;

    if (
      component instanceof CropSelectionTopLeftComponent ||
      component instanceof CropSelectionBottomRightComponent
    ) {
      return "nwse-resize";
    }

    if (
      component instanceof CropSelectionTopRightComponent ||
      component instanceof CropSelectionBottomLeftComponent
    ) {
      return "nesw-resize";
    }

    if (
      component instanceof CropSelectionTopComponent ||
      component instanceof CropSelectionBottomComponent
    ) {
      return "ns-resize";
    }

    if (
      component instanceof CropSelectionLeftComponent ||
      component instanceof CropSelectionRightComponent
    ) {
      return "ew-resize";
    }

    if (component instanceof CropSelectionComponent) {
      return "move";
    }

    // Some tools (e.g. canvas resize) might be picked accidentally; keep default.
    if (component instanceof CanvasResizeSelectionComponent) {
      return DEFAULT_CURSOR;
    }

    return DEFAULT_CURSOR;
  }
}

export default CropTool;
